import { useTranslation } from '../presentation/common/useTranslation.js';
import Skeleton from './Skeleton';
import SkeletonDelay from './SkeletonDelay';
import SkeletonContent, { DashboardContentSkeleton, GuildHomeSkeleton, SkeletonLines } from './SkeletonContent';
import { dashboardLayout } from './dashboard/dashboardLayout';
import '../styles/guildRoom.css';
import '../pages/ClassesAndRanks.css';

function Lines({ count = 3 }) {
  return <div className="skeleton-lines">{Array.from({ length: count }, (_, index) =>
    <Skeleton key={index} className={index === count - 1 ? 'skeleton-line skeleton-line-short' : 'skeleton-line'} />
  )}</div>;
}

export function SkeletonCard({ image = true }) {
  return <div className="skeleton-card" aria-hidden="true">
    {image && <Skeleton className="skeleton-cover" />}
    <div className="skeleton-card-body"><Skeleton className="skeleton-heading" /><Lines />
      <Skeleton className="skeleton-button" /></div>
  </div>;
}

function Content({ variant, count, grid, fieldColumns, showProfileBanner = true }) {
  const specific = SkeletonContent({ variant, count, grid, fieldColumns });
  if (specific) return specific;
  if (variant === 'class-showcase') return <div className="class-showcase-list">
    {Array.from({ length: 3 }, (_, index) => <div className="class-showcase" key={index}>
      <Skeleton className="class-showcase-scene" />
      <div className="class-showcase-content space-y-5"><Skeleton className="h-4 w-40" /><Skeleton className="h-14 w-3/4" /><Lines count={4} />
        <div className="class-showcase-perks">{Array.from({ length: 3 }, (_, perk) => <div key={perk} className="class-showcase-perk"><Skeleton className="h-6 w-6" /><div className="flex-1 space-y-3"><Skeleton className="h-4 w-2/3" /><Lines count={2} /></div></div>)}</div>
      </div>
    </div>)}
  </div>;
  if (variant === 'map') return <div className="world-map-layout"><div className="world-map-region"><div className="world-map-frame"><div className="world-map-canvas skeleton-map">
    <Skeleton className="skeleton-map-canvas" />
    <div className="skeleton-map-controls"><Skeleton className="skeleton-input" /><Lines count={2} /></div>
  </div></div></div><aside className="world-map-tools hidden min-[851px]:block"><div className="skeleton-card skeleton-card-body"><Skeleton className="skeleton-heading" /><Lines /></div></aside></div>;
  if (variant === 'form' || variant === 'auth') return <div className="skeleton-form skeleton-card">
    {Array.from({ length: count }, (_, index) => <div key={index} className="skeleton-field">
      <Skeleton className="skeleton-label" /><Skeleton className={index === 2 ? 'skeleton-textarea' : 'skeleton-input'} />
    </div>)}<Skeleton className="skeleton-button" />
  </div>;
  if (variant === 'article') return <div className="skeleton-card skeleton-article">
    <Skeleton className="skeleton-cover" /><Lines count={5} /><Lines count={4} />
  </div>;
  if (variant === 'talents') return <div className="skeleton-card skeleton-talent-tree">
    <div className="skeleton-talent-branches">{Array.from({ length: 3 }, (_, branch) =>
      <div className="skeleton-talent-branch" key={branch}><Skeleton className="h-4 w-2/3 mx-auto" />{Array.from({ length: 3 }, (_, node) =>
        <div className="skeleton-talent-level" key={node}><Skeleton className="skeleton-talent-connector" /><Skeleton className="skeleton-talent-node" /></div>
      )}</div>
    )}</div>
  </div>;
  if (variant === 'cards') return <div className="skeleton-grid skeleton-grid-cards">
    {Array.from({ length: count }, (_, index) => <SkeletonCard key={index} />)}
  </div>;
  if (variant === 'profile' || variant === 'dashboard') return <>
    {showProfileBanner && <div className="skeleton-card skeleton-profile"><Skeleton className="skeleton-profile-banner" />
      <div className="skeleton-profile-identity"><Skeleton className="skeleton-avatar" /><Lines /></div>
    </div>}
    <div className="skeleton-stats">{Array.from({ length: 4 }, (_, index) => <div className="skeleton-card skeleton-stat" key={index}>
      <Skeleton className="skeleton-label" /><Skeleton className="skeleton-heading" />
    </div>)}</div>
    <div className="skeleton-grid skeleton-grid-panels"><SkeletonCard image={false} /><SkeletonCard image={false} /></div>
  </>;
  if (variant === 'chat') return <div className="skeleton-chat">
    <div className="skeleton-card"><Rows count={count} /></div>
    <div className="skeleton-card skeleton-chat-messages"><Lines count={2} /><Lines /><Skeleton className="skeleton-input" /></div>
  </div>;
  if (variant === 'table') return <div className="skeleton-card skeleton-table"><Rows count={count} table /></div>;
  return <Rows count={count} />;
}

function Rows({ count, table = false }) {
  return <div className="skeleton-rows">{Array.from({ length: count }, (_, index) =>
    <div className={`skeleton-row ${table ? 'skeleton-table-row' : 'skeleton-card'}`} key={index}>
      <Skeleton className="skeleton-row-avatar" /><Lines count={2} />
      <Skeleton className="skeleton-row-meta" />
    </div>
  )}</div>;
}

/** Shared data-loading state. Keep headings and controls outside when already available. */
export default function PageSkeleton(props) {
  return <SkeletonDelay><PageSkeletonContent {...props} /></SkeletonDelay>;
}

function PageSkeletonContent({ variant = 'list', count = variant === 'cards' ? 6 : 4, grid, fieldColumns, fullPage = false, layout = 'content', className = '', label }) {
  const { t } = useTranslation();
  const status = { role: 'status', 'aria-busy': true, 'aria-label': label || t('common.loading', 'Lädt …'), 'data-skeleton': variant };
  if (layout === 'dashboard') return <main className={`${dashboardLayout.page} page-skeleton ${className}`} {...status}>
    <span className="skeleton-sr-only">{status['aria-label']}</span>
    <div className={dashboardLayout.grid} aria-hidden="true"><section className={`${dashboardLayout.story} skeleton-story`}><Skeleton className="skeleton-story-image" /><div className="relative z-10 space-y-4 max-w-md"><Skeleton className="h-4 w-32" /><Skeleton className="h-9 w-3/4" /><SkeletonLines /></div></section><section className={dashboardLayout.content}><DashboardContentSkeleton /></section></div>
  </main>;
  if (variant === 'guild-home') return <div className={`page-skeleton skeleton-guild-home ${className}`} {...status}><span className="skeleton-sr-only">{status['aria-label']}</span><div aria-hidden="true"><GuildHomeSkeleton /></div></div>;
  const room = layout === 'room';
  const hasStory = room || layout === 'world';
  const content = <div className="skeleton-layout" aria-hidden="true">
    {fullPage && !['map', 'chat'].includes(variant) && <div className="skeleton-page-header"><Skeleton className="skeleton-heading" /><Lines count={2} /></div>}
    <Content variant={variant} count={count} grid={grid} fieldColumns={fieldColumns} showProfileBanner={!hasStory} />
  </div>;
  return <div className={`page-skeleton ${fullPage ? 'page-skeleton-full' : ''} ${hasStory ? `world-page page-skeleton-story-layout page-skeleton-${layout}` : ''} ${room ? 'guild-room-page' : ''} ${className}`}
    role="status" aria-busy="true" aria-label={label || t('common.loading', 'Lädt …')} data-skeleton={variant}>
    <span className="skeleton-sr-only">{label || t('common.loading', 'Lädt …')}</span>
    {hasStory ? <>
      <aside className={`${room ? 'guild-room-scene' : 'world-story'} skeleton-story`} aria-hidden="true">
        <Skeleton className="skeleton-story-image" />
        <div className="skeleton-story-copy"><Skeleton className="skeleton-heading" /><Skeleton className="skeleton-heading skeleton-line-short" />
          <Lines count={3} /><div className="skeleton-profile-identity"><Skeleton className="skeleton-avatar" /><Lines count={2} /></div>
        </div>
      </aside>
      {room ? <div className="guild-room-workspace"><div className="guild-room-navigation" aria-hidden="true"><Skeleton className="h-10 w-32" /><Skeleton className="h-10 w-40" /></div><div className="guild-room-mobile-header" aria-hidden="true"><Skeleton className="w-full h-full absolute inset-0" /></div><div className="guild-room-content">{content}</div></div> : <div className="world-right"><div className="world-content">{content}</div></div>}
    </> : content}
  </div>;
}
